/** The MMTG files the web renderer reads, by element type name. */
export type GeometryType = 'connections' | 'stations' | 'additionals' | 'transitions'

/**
 * One MMTG file set of a bundle: the day map in its `light` and `dark`
 * themes, or the late-night frame (`night`, TASK-1136 — the `*-night.mmtg`
 * files, drawn on the dark palette from `paths.night`).
 */
export type MapView = 'light' | 'dark' | 'night'

/** One view's markup per element type: entry id → SVG markup. */
export type TypeMarkup = Readonly<Record<GeometryType, ReadonlyMap<string, string>>>

/** The ids one view's MMTG files must hold, per element type. */
export interface ViewIds {
  readonly stopIds: ReadonlySet<string>
  readonly connectionIds: ReadonlySet<string>
  /** Transfers with geometry; hidden ones (empty paths) are graph-only. */
  readonly drawnTransferIds: ReadonlySet<string>
}

/**
 * What a v2 schema says each MMTG file must contain — the inputs of the
 * signature check and the referential check of a map bundle (spec §3.5
 * steps 4–5). The writer (`metro-server` `pkg/mapcore/artifact`) emits one
 * entry per drawable with its `paths[view]`: connections (a `direction`
 * arrow is drawn inside its connection's entry, so the connection's markup is
 * its line followed by its arrows), stops, additionals except `direction`,
 * drawn transfers (a visible transfer that draws a whole interchange also
 * gets a `baseOnly` entry with the interchange's markup). The day views hold
 * every drawable; the night view only those with `paths.night` (a transfer
 * also when only its `interchange.night` is drawn).
 */
export interface SchemaMarkup {
  markup(view: MapView): TypeMarkup
  /** Transfer id → its interchange markup, for the `baseOnly` entries. */
  interchanges(view: MapView): ReadonlyMap<string, string>
  ids(view: MapView): ViewIds
  /** The schema has a late-night frame: some drawable carries `paths.night`. */
  readonly hasNight: boolean
}

interface Drawable {
  readonly id: string
  readonly paths: Partial<Record<MapView, string>>
  readonly kind?: string
  readonly connectionId?: string
  readonly interchange?: Partial<Record<MapView, string>>
}

function records(schema: Record<string, unknown>, key: string): readonly Drawable[] {
  const value = schema[key]
  if (!Array.isArray(value)) throw new Error(`schema has no ${key}`)
  return value.filter(
    (r): r is Drawable =>
      typeof r === 'object' &&
      r !== null &&
      typeof (r as Drawable).id === 'string' &&
      typeof (r as Drawable).paths === 'object' &&
      (r as Drawable).paths !== null,
  )
}

/** The night view draws only what has night markup; a day view draws everything. */
function drawnIn(view: MapView, r: Drawable): boolean {
  return view !== 'night' || (r.paths.night ?? '') !== ''
}

/** A transfer the view's transitions file holds: its own markup or its interchange's. */
function transferDrawnIn(view: MapView, t: Drawable): boolean {
  if (view === 'night') return (t.paths.night ?? '') !== '' || (t.interchange?.night ?? '') !== ''
  return (t.paths.light ?? '') !== ''
}

function byId(list: readonly Drawable[], view: MapView): Map<string, string> {
  const out = new Map<string, string>()
  for (const r of list) if (!out.has(r.id)) out.set(r.id, r.paths[view] ?? '')
  return out
}

/** Reads the drawables of a parsed v2 `schema.json`; throws when a collection is missing. */
export function schemaMarkup(schema: unknown): SchemaMarkup {
  if (typeof schema !== 'object' || schema === null) throw new Error('schema is not an object')
  const raw = schema as Record<string, unknown>
  const stops = records(raw, 'stops')
  const connections = records(raw, 'connections')
  const additionals = records(raw, 'additionals')
  const transfers = records(raw, 'transfers')

  const inView = (view: MapView, list: readonly Drawable[]): readonly Drawable[] =>
    list.filter((r) => drawnIn(view, r))
  const drawnTransfers = (view: MapView): readonly Drawable[] =>
    transfers.filter((t) => transferDrawnIn(view, t))

  return {
    markup(view) {
      const arrows = new Map<string, string>()
      for (const a of inView(view, additionals)) {
        if (a.kind !== 'direction' || a.connectionId === undefined) continue
        arrows.set(a.connectionId, (arrows.get(a.connectionId) ?? '') + (a.paths[view] ?? ''))
      }
      const lines = byId(inView(view, connections), view)
      for (const [id, path] of lines) lines.set(id, path + (arrows.get(id) ?? ''))
      return {
        connections: lines,
        stations: byId(inView(view, stops), view),
        additionals: byId(
          inView(view, additionals).filter((a) => a.kind !== 'direction'),
          view,
        ),
        transitions: byId(drawnTransfers(view), view),
      }
    },
    interchanges(view) {
      const out = new Map<string, string>()
      for (const t of transfers) {
        if (t.interchange !== undefined) out.set(t.id, t.interchange[view] ?? '')
      }
      return out
    },
    ids(view) {
      return {
        stopIds: new Set(inView(view, stops).map((s) => s.id)),
        connectionIds: new Set(inView(view, connections).map((c) => c.id)),
        drawnTransferIds: new Set(drawnTransfers(view).map((t) => t.id)),
      }
    },
    hasNight: [stops, connections, additionals, transfers].some((list) =>
      list.some((r) => (r.paths.night ?? '') !== ''),
    ),
  }
}
